import { useParams, Link } from 'react-router-dom'
import { useState, useEffect, useMemo, useTransition } from 'react'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Navbar } from '@/components/navbar'
import { NewsCard, NewsCardSkeleton } from '@/components/news-card'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { ArrowLeft, Loader2, ExternalLink, Bell, BellOff } from 'lucide-react'
import { motion } from 'framer-motion'
import { getViewMode } from '@/lib/utils'

export function ProviderPage() {
  const { slug } = useParams()
  const [viewMode, setViewMode] = useState(getViewMode())
  const [isFollowing, setIsFollowing] = useState(false)
  const [isPending, startTransition] = useTransition()

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '200px',
  })

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['provider', slug],
    queryFn: ({ pageParam = 1 }) => newsApi.getByProvider(slug, pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined
      
      // Extract page number from URL
      try {
        const url = new URL(lastPage.data.next)
        const page = url.searchParams.get('page')
        return page ? parseInt(page, 10) : undefined
      } catch {
        return undefined
      }
    },
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  })

  const articles = useMemo(() => {
    return data?.pages.flatMap(page => page.data.results) || []
  }, [data?.pages])
  
  const providerInfo = articles[0]?.feeder

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      startTransition(() => {
        fetchNextPage()
      })
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-24 bg-gray-200 rounded animate-shimmer mb-6" />
            <div className="bg-white rounded-xl p-8 mb-8">
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full animate-shimmer" />
                <div className="flex-1 space-y-3">
                  <div className="h-8 bg-gray-200 rounded animate-shimmer w-48" />
                  <div className="h-4 bg-gray-200 rounded animate-shimmer w-full" />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <NewsCardSkeleton key={i} variant={viewMode} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !providerInfo) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar onViewModeChange={setViewMode} />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-red-50 rounded-full p-8 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Provider Not Found</h1>
          <p className="text-gray-600 mb-6">The news provider you're looking for doesn't exist.</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onViewModeChange={setViewMode} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Feed</span>
          </Link>

          {/* Provider header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-8 shadow-sm mb-8"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {providerInfo.logo && (
                <img
                  src={providerInfo.logo}
                  alt={providerInfo.display_name || providerInfo.name}
                  className="w-24 h-24 rounded-full shadow-md"
                />
              )}
              
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {providerInfo.display_name || providerInfo.name}
                </h1>
                {providerInfo.description && (
                  <p className="text-gray-600 mb-4">
                    {providerInfo.description}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  <Button
                    variant={isFollowing ? "outline" : "default"}
                    size="sm"
                    onClick={() => setIsFollowing(!isFollowing)}
                  >
                    {isFollowing ? (
                      <>
                        <BellOff className="w-4 h-4 mr-2" />
                        Unfollow
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-2" />
                        Follow
                      </>
                    )}
                  </Button>
                  {providerInfo.website && (
                    <a href={providerInfo.website} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Visit Website
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{articles.length}+</div>
                <div className="text-sm text-gray-500">Articles</div>
              </div>
            </div>
          </motion.div>

          {/* Articles from this provider */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Latest from {providerInfo.display_name || providerInfo.name}</h2>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500">No articles found from this provider.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((article, index) => (
                <NewsCard
                  key={article.alias || article.id}
                  article={article}
                  variant={viewMode}
                  index={index}
                />
              ))}
              {/* Loading next page skeletons */}
              {(isFetchingNextPage || isPending) && (
                <>
                  {[...Array(3)].map((_, i) => (
                    <NewsCardSkeleton key={`skeleton-${i}`} variant={viewMode} />
                  ))}
                </>
              )}
            </div>
          )}

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-4">
            {!hasNextPage && articles.length > 0 && (
              <p className="text-sm text-gray-500 text-center">You've reached the end</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
